import React, { lazy, useEffect } from "react";
import { Toaster } from "sonner";
import MetropoleSection from "@/components/MetropoleSection";
import SolutionsSection from "@/components/SolutionsSection";
import PromoBanner from "@/components/PromoBanner";
import { consumePendingScrollTarget, scrollToId } from "@/lib/scroll";
import "@/App.css";
import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
import EditorialTicker from "@/components/EditorialTicker";
import TargetAudienceSection from "@/components/TargetAudienceSection";
import AdminPage from "@/components/AdminPage";
import LegalPage from "@/components/LegalPage";
import Footer from "@/components/Footer";
import DeferredSection from "@/components/DeferredSection";

const WhyUsSection = lazy(() => import("@/components/WhyUsSection"));
const TechBentoSection = lazy(() => import("@/components/TechBentoSection"));
const ProcessTimelineSection = lazy(() => import("@/components/ProcessTimelineSection"));
const ReportExampleSection = lazy(() => import("@/components/ReportExampleSection"));
const ServicesSection = lazy(() => import("@/components/ServicesSection"));
const PricingSection = lazy(() => import("@/components/PricingSection"));
const ContactSection = lazy(() => import("@/components/ContactSection"));
const CityPage = lazy(() => import("@/pages/CityPage"));
const AgencyPage = lazy(() => import("@/pages/AgencyPage"));

const CITY_SLUGS = [
  "lyon",
  "villeurbanne",
  "bron",
  "vaulx-en-velin",
  "venissieux",
  "caluire-et-cuire",
  "decines-charpieu",
  "meyzieu",
  "saint-priest",
  "oullins-pierre-benite",
];

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(error) {
    console.error("UI error:", error);
  }
  render() {
    if (this.state.failed) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-night p-8 text-center text-white">
          <p>
            Une erreur est survenue. Rechargez la page ou appelez le 06 22 85 76 87.
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

function LandingPage() {
  useEffect(() => {
    const target = consumePendingScrollTarget();
    if (!target) return;
    // Pas de cleanup : StrictMode double-monte en dev et annulerait le timer.
    // Le 2e montage trouve sessionStorage déjà vide et ne reprogramme rien.
    setTimeout(() => scrollToId(target), 400);
    // Les sections différées prennent leur vraie hauteur pendant le scroll :
    // on recale la position une fois le layout stabilisé (sauf interaction).
    let userMoved = false;
    const cancel = () => {
      userMoved = true;
    };
    window.addEventListener("wheel", cancel, { once: true, passive: true });
    window.addEventListener("touchstart", cancel, { once: true, passive: true });
    setTimeout(() => {
      window.removeEventListener("wheel", cancel);
      window.removeEventListener("touchstart", cancel);
      if (userMoved) return;
      const el = document.getElementById(target);
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY - 80;
      if (Math.abs(window.scrollY - top) > 140) {
        window.scrollTo({ top, behavior: "auto" });
      }
    }, 1600);
  }, []);

  return (
    <div className="relative min-h-screen overflow-x-clip bg-night text-white">
      <PromoBanner />
      <Navbar />
      <main>
        <HeroSection />
        <EditorialTicker />
        <TargetAudienceSection />
        <SolutionsSection />
        <MetropoleSection />
        <DeferredSection id="pourquoi" minHeight={860}>
          <WhyUsSection />
        </DeferredSection>
        <DeferredSection id="technologie" minHeight={940}>
          <TechBentoSection />
        </DeferredSection>
        <DeferredSection id="deroule" minHeight={780}>
          <ProcessTimelineSection />
        </DeferredSection>
        <DeferredSection id="exemple" minHeight={1020}>
          <ReportExampleSection />
        </DeferredSection>
        <DeferredSection id="services" minHeight={980}>
          <ServicesSection />
        </DeferredSection>
        <DeferredSection id="tarifs" minHeight={1700}>
          <PricingSection />
        </DeferredSection>
        <DeferredSection id="contact" minHeight={880}>
          <ContactSection />
        </DeferredSection>
      </main>
      <Footer />
    </div>
  );
}

const path = typeof window !== "undefined" ? window.location.pathname : "/";
const isAdminRoute = path.startsWith("/admin");
const LEGAL_ROUTES = ["/mentions-legales", "/confidentialite", "/cgv"];
const isLegalRoute = LEGAL_ROUTES.some((r) => path === r);
const cityMatch = path.match(/^\/etat-des-lieux-([a-z-]+)$/);
const citySlug = cityMatch && CITY_SLUGS.includes(cityMatch[1]) ? cityMatch[1] : null;
const isAgencyRoute = path === "/agences-immobilieres";

export default function App() {
  if (isAdminRoute) {
    return <AdminPage />;
  }

  if (isLegalRoute) {
    return <LegalPage />;
  }

  if (citySlug) {
    return (
      <ErrorBoundary>
        <CityPage slug={citySlug} />
        <Toaster theme="dark" position="top-center" richColors />
      </ErrorBoundary>
    );
  }

  if (isAgencyRoute) {
    return (
      <ErrorBoundary>
        <AgencyPage />
        <Toaster theme="dark" position="top-center" richColors />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <LandingPage />
      <Toaster theme="dark" position="top-center" richColors />
    </ErrorBoundary>
  );
}
